/* Photo uploader using Cloudinary signed upload (admin-only). */
import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X, Move, ImageIcon } from "lucide-react";
import { cloudinaryThumb } from "../lib/publicUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHdr() {
  const t = localStorage.getItem("mahasa_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function PhotoUploader({ photos = [], onChange, folder = "cars", compact = false }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);

  async function uploadFile(file) {
    // 1. Get signature from backend
    const sigRes = await axios.get(`${API}/cloudinary/signature?folder=${folder}`, { headers: authHdr() });
    const sig = sigRes.data;
    // 2. Upload directly to Cloudinary
    const fd = new FormData();
    fd.append("file", file);
    fd.append("api_key", sig.api_key);
    fd.append("timestamp", sig.timestamp);
    fd.append("signature", sig.signature);
    fd.append("folder", sig.folder);
    const up = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, {
      method: "POST",
      body: fd,
    });
    const json = await up.json();
    if (!json.secure_url) throw new Error(json.error?.message || "Upload fejlede");
    return { url: json.secure_url, public_id: json.public_id };
  }

  async function handleFiles(files) {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (!f.type.startsWith("image/")) continue;
        if (f.size > 12 * 1024 * 1024) {
          toast.error(`${f.name} er over 12 MB`);
          continue;
        }
        const photo = await uploadFile(f);
        uploaded.push(photo);
      }
      if (uploaded.length) {
        onChange([...(photos || []), ...uploaded]);
        toast.success(`${uploaded.length} billede${uploaded.length === 1 ? "" : "r"} uploadet`);
      }
    } catch (e) {
      toast.error("Upload fejlede: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function removePhoto(idx) {
    const photo = photos[idx];
    const newList = photos.filter((_, i) => i !== idx);
    onChange(newList);
    // Best-effort delete from Cloudinary
    if (photo?.public_id) {
      try { await axios.delete(`${API}/cloudinary/${photo.public_id}`, { headers: authHdr() }); }
      catch { /* non-fatal */ }
    }
  }

  function move(idx, dir) {
    const target = idx + dir;
    if (target < 0 || target >= photos.length) return;
    const next = photos.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  }

  return (
    <div data-testid="photo-uploader">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
      />
      <div className={compact ? "grid grid-cols-5 sm:grid-cols-8 gap-1.5 mb-2" : "grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3"}>
        {photos.map((p, i) => (
          <div key={p.public_id || p.url} className="relative group aspect-square rounded-md overflow-hidden bg-zinc-800" data-testid={`photo-${i}`}>
            <img src={cloudinaryThumb(p.url, compact ? "c_fill,w_150,q_auto,f_auto" : "c_fill,w_300,q_auto,f_auto")} alt="" className="w-full h-full object-cover"/>
            {i === 0 && <span className={`absolute top-0.5 left-0.5 bg-amber-500 text-zinc-900 font-bold rounded ${compact ? "text-[8px] px-1 py-0" : "text-[9px] px-1.5 py-0.5"}`}>PRIMÆR</span>}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className={`bg-white/15 hover:bg-white/25 rounded disabled:opacity-30 ${compact ? "p-1" : "p-1.5"}`} title="Flyt op" data-testid={`photo-up-${i}`}>
                <Move size={compact ? 10 : 12} className="rotate-180"/>
              </button>
              <button onClick={() => move(i, 1)} disabled={i === photos.length - 1} className={`bg-white/15 hover:bg-white/25 rounded disabled:opacity-30 ${compact ? "p-1" : "p-1.5"}`} title="Flyt ned" data-testid={`photo-down-${i}`}>
                <Move size={compact ? 10 : 12}/>
              </button>
              <button onClick={() => removePhoto(i)} className={`bg-red-500/80 hover:bg-red-500 rounded ${compact ? "p-1" : "p-1.5"}`} title="Slet" data-testid={`photo-del-${i}`}>
                <X size={compact ? 10 : 12}/>
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className={`aspect-square rounded-md border-2 border-dashed border-zinc-700 hover:border-amber-500 text-zinc-500 hover:text-amber-500 flex flex-col items-center justify-center gap-1 transition disabled:opacity-50 ${compact ? "text-[9px]" : ""}`}
          data-testid="photo-add-btn"
        >
          {busy ? <span className="text-xs">…</span> : (<>
            <Upload size={compact ? 14 : 20}/>
            <span className={compact ? "text-[9px]" : "text-[10px]"}>Tilføj</span>
          </>)}
        </button>
      </div>
      {photos.length === 0 && (
        <p className="text-zinc-500 text-xs flex items-center gap-1"><ImageIcon size={12}/> Ingen billeder endnu. Det første du tilføjer bliver primær.</p>
      )}
    </div>
  );
}
